import QRCode, { QRCodeToDataURLOptions } from 'qrcode';
import { FileError } from 'react-dropzone';
import { slugIt } from '../string';

export function getNormalizedFileName(fileName: string): string {
  const extension = fileName.split('.').pop();
  const firstPart = fileName.substring(0, fileName.lastIndexOf(extension!));
  const firstPartNormalized = firstPart.replace(/[^A-Za-z0-9_\\-]/g, '');

  return [firstPartNormalized, extension].join('.');
}

export interface Validator<T, E extends FileError> {
  (fileName: string, validationContext?: T, normalize?: (toNormalize: string) => string): E | null
}

export interface FileAlreadyExistsError extends FileError {
  message: FileError['message'],
  code: 'file-already-exists'
}

export const duplicatedFileNameValidator: Validator<string[], FileAlreadyExistsError> = (fileName, validationContext, normalize) => {
  const normalizedFileName = normalize ? normalize(fileName) : fileName;
  const normalizedValidationContext = validationContext?.map((existingFileName) => (normalize ? normalize(existingFileName) : existingFileName));

  const fileAlreadyExists = normalizedValidationContext?.some((existingFileName) => existingFileName === normalizedFileName);

  if (fileAlreadyExists) {
    const fileAlreadyExistsError: FileAlreadyExistsError = {
      message: 'File already exists',
      code: 'file-already-exists',
    };

    return fileAlreadyExistsError;
  }

  return null;
};

export const downloadFile = (
  data: BlobPart,
  mimeType: string,
  fileName: string = 'file',
) => {
  const blob = new Blob([data], { type: mimeType });
  const fileURL = URL.createObjectURL(blob);

  const anchor = document.createElement('a');
  anchor.href = fileURL;
  anchor.download = fileName;

  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);

  URL.revokeObjectURL(fileURL);
};

/**
 * Fetches an image from the given URL and returns it as a base64-encoded string
 * along with a filename derived from the URL path.
 */
export async function imageUrlToBase64(url: string): Promise<{ base64: string, name: string }> {
  const response = await fetch(url);
  const buffer = await response.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  const base64 = btoa(binary);
  const rawSegment = url.split('/').pop()?.split('?')[0] || '';
  const name = decodeURIComponent(rawSegment).split('/').pop() || 'image';
  return { base64, name };
}

/**
 * Generates a QR code image encoding the given value, returning it as a
 * base64-encoded PNG string along with a filename.
 */
export async function qrValueToBase64(
  value: string,
  options?: QRCodeToDataURLOptions,
): Promise<{ base64: string, name: string }> {
  const dataUrl = await QRCode.toDataURL(value, {
    errorCorrectionLevel: 'M',
    margin: 1,
    width: 512,
    ...options,
  });

  const base64 = dataUrl.split(',')[1];

  const name = `qr-${slugIt(value)}.png`;
  return { base64, name };
}

/**
 * Downloads multiple files sequentially from the provided URLs.
 * Fetches each file as a blob to bypass cross-origin `download` attribute restrictions.
 * Errors on individual URLs are caught and logged without blocking remaining downloads.
 *
 * @param urls - List of file URLs to download
 * @param delayMs - Delay in milliseconds between each download (default: 300)
 */
export const downloadFilesFromUrls = async (urls: string[], delayMs = 300): Promise<void> => {
  await urls.reduce(async (prev, url, i) => {
    await prev;
    await new Promise<void>((resolve) => { setTimeout(resolve, i === 0 ? 0 : delayMs); });

    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      const blob = await response.blob();
      const filename = url.split('/').pop()?.split('?')[0] || 'export';

      downloadFile(blob, blob.type, filename);
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(`Download failed for ${url}:`, error);
    }
  }, Promise.resolve());
};
